import type { DestroyRef, WritableSignal } from '@angular/core';
import type { Subscription } from 'rxjs';
import type { Uuid } from '@common/types';
import type { WebLegacyWidgetEntity } from '@web/types';

import { Injectable, Injector, inject, runInInjectionContext, signal, untracked } from '@angular/core';
import { filter } from 'rxjs/operators';

import { Widgets } from '@common/enums';

import type {
    AccordionEntity,
    ButtonEntity,
    EduLoginButtonEntity,
    FigureEntity,
    FormWidgetEntity,
    ImageEntity,
    LogoEntity,
    LoginEduWidgetEntity,
    PaymentFormWidgetEntity,
    SocialEntity,
    TextEntity,
    VideoEntity,
} from '@sites/entity-core';
import {
    createAccordion,
    createButton,
    createEduLoginButton,
    createFigure,
    createImage,
    createLogo,
    createLoginEduWidget,
    createPaymentFormWidget,
    createSocial,
    createText,
    createVideo,
} from '@sites/entity-core';
import type {
    AccordionForm,
    BaseLegacyEntityFormOptions,
    ButtonForm,
    EduLoginButtonForm,
    FigureForm,
    FormWidgetForm,
    ImageForm,
    LogoForm,
    LoginEduForm,
    PaymentFormWidgetForm,
    SocialForm,
    TextForm,
    VideoForm,
} from '@sites/entity-signal-form';
import {
    createAccordionForm,
    createButtonForm,
    createEduLoginButtonForm,
    createFigureForm,
    createFormWidgetForm,
    createImageForm,
    createLogoForm,
    createLoginEduForm,
    createPaymentFormWidgetForm,
    createSocialForm,
    createTextForm,
    createVideoForm,
} from '@sites/entity-signal-form';

import { WidgetsFacade } from '@web/store/page-structure/widgets/widgets.facade';
import { HistoryFacade } from '@web/store/history/history.facade';

import { WebWidgetLoadFillsService } from './web-widget-load-fills.service';

/**
 * Union of every signal-form-capable widget's entity / form. Extend both
 * unions (and the factory map below) when migrating the next widget.
 */
export type WidgetSignalEntity =
    | VideoEntity
    | ButtonEntity
    | EduLoginButtonEntity
    | FigureEntity
    | FormWidgetEntity
    | ImageEntity
    | LogoEntity
    | LoginEduWidgetEntity
    | PaymentFormWidgetEntity
    | SocialEntity
    | TextEntity
    | AccordionEntity;
export type WidgetSignalForm =
    | VideoForm
    | ButtonForm
    | EduLoginButtonForm
    | FigureForm
    | FormWidgetForm
    | ImageForm
    | LogoForm
    | LoginEduForm
    | PaymentFormWidgetForm
    | SocialForm
    | TextForm
    | AccordionForm;

/**
 * Placement context the form can't own, supplied by the widget host when it
 * calls `form.copy(payload)` / `form.remove(payload)`. The entity itself comes
 * from the form's current value; only its position in the page tree travels
 * through here.
 */
export type WidgetCopyPayload = {
    index: number;
};
export type WidgetRemovePayload = {
    index: number;
    skipHistory?: boolean;
    skipUnselectEntity?: boolean;
};

type WidgetSignalFormFactory = (options: BaseLegacyEntityFormOptions<any>) => WidgetSignalForm;

/**
 * Widget-name → form-factory dispatch table. Each factory is itself a
 * variant dispatcher (default vs zero), picking per `initialData.type`.
 */
const FORM_FACTORIES: Partial<Record<Widgets, WidgetSignalFormFactory>> = {
    [Widgets.VIDEO]: createVideoForm,
    [Widgets.BUTTON]: createButtonForm,
    [Widgets.EDU_LOGIN_BUTTON]: createEduLoginButtonForm,
    [Widgets.FIGURE]: createFigureForm,
    [Widgets.FORM]: createFormWidgetForm,
    [Widgets.PAYMENT_FORM]: createPaymentFormWidgetForm,
    [Widgets.SOCIAL_NETWORKS]: createSocialForm,
    [Widgets.TEXT]: createTextForm,
    [Widgets.IMAGE]: createImageForm,
    [Widgets.LOGO]: createLogoForm,
    [Widgets.LOGIN_EDU]: createLoginEduForm,
    [Widgets.ACCORDION]: createAccordionForm,
};

/**
 * Widget-name → default-entity factory. Mirrors {@link FORM_FACTORIES}: each
 * returns a fully-populated entity for the given placement variant, used to
 * backfill fields absent from older persisted widgets (e.g. `hideOnTablet`,
 * `position.tabletX`, `animation`) before the signal form is built. Without
 * this, the form's FieldTree never materialises nodes for the missing keys and
 * reads/writes throw `undefined is not a function`.
 */
const DEFAULT_FACTORIES: Partial<Record<Widgets, (variant?: 'zero') => WidgetSignalEntity>> = {
    [Widgets.VIDEO]: (variant) => createVideo(variant as any),
    [Widgets.BUTTON]: (variant) => createButton(variant as any),
    [Widgets.EDU_LOGIN_BUTTON]: (variant) => createEduLoginButton(variant as any),
    [Widgets.FIGURE]: () => createFigure(),
    [Widgets.PAYMENT_FORM]: (variant) => createPaymentFormWidget(variant as any),
    [Widgets.SOCIAL_NETWORKS]: (variant) => createSocial(variant as any),
    [Widgets.TEXT]: (variant) => createText(variant as any),
    [Widgets.IMAGE]: (variant) => createImage(variant as any),
    [Widgets.LOGO]: () => createLogo(),
    // No `texts` here on purpose: this factory only backfills keys the persisted
    // entity is missing, and the host model owns the translated labels.
    [Widgets.LOGIN_EDU]: (variant) => createLoginEduWidget(variant as any),
    [Widgets.ACCORDION]: () => createAccordion(),
};

/**
 * A `mobile*`/`tablet*` switch at the root of `data` that the widget never had is seeded `null`, not with
 * the factory's default: renderers read an empty one as "inherit", and the default would be written back
 * with the first edit of the widget (#69719 bug 19 — images left-aligned on tablet).
 */
function withInheritedDeviceOverrides(defaults: WidgetSignalEntity): WidgetSignalEntity {
    const data = defaults.data as Record<string, unknown> | undefined;
    if (!data) {
        return defaults;
    }

    const isDeviceOverride = (key: string, value: unknown) =>
        /^(mobile|tablet)[A-Z]/.test(key) && (value === null || typeof value !== 'object');

    return {
        ...defaults,
        data: Object.fromEntries(
            Object.entries(data).map(([key, value]) => [key, isDeviceOverride(key, value) ? null : value])
        ),
    } as WidgetSignalEntity;
}

/** Backfills a store entity with its variant defaults — see {@link DEFAULT_FACTORIES}. */
export function backfillWidgetEntity(entity: WidgetSignalEntity): WidgetSignalEntity {
    const createDefaults = DEFAULT_FACTORIES[entity.name as Widgets];
    if (!createDefaults) {
        return entity;
    }

    const variant = (entity as { type?: unknown }).type === 'zero' ? 'zero' : undefined;
    return withDefaults(withInheritedDeviceOverrides(createDefaults(variant)), entity);
}

/**
 * Deep-merge `entity` onto `defaults`: the entity's value wins for every key it
 * defines, and keys it omits fall back to the default. Objects recurse; arrays
 * and primitives are taken wholesale from the entity (a present array is treated
 * as authoritative). Returns a fresh object — never mutates either input.
 */
function withDefaults<T>(defaults: T, entity: T): T {
    if (entity === null || entity === undefined) {
        return defaults;
    }
    if (
        typeof entity !== 'object' ||
        typeof defaults !== 'object' ||
        defaults === null ||
        Array.isArray(entity) ||
        Array.isArray(defaults)
    ) {
        return entity;
    }

    const defaultsObj = defaults as Record<string, any>;
    const entityObj = entity as Record<string, any>;
    const result: Record<string, any> = { ...entityObj };

    for (const key of Object.keys(defaultsObj)) {
        if (entityObj[key] === undefined) {
            result[key] = defaultsObj[key];
        } else {
            result[key] = withDefaults(defaultsObj[key], entityObj[key]);
        }
    }

    return result as T;
}

/**
 * The slice of a widget entity an undo/redo entry has to carry. `data` and
 * `displayId` always (an absent id is recorded too, so undoing a first custom
 * id clears it); `styles` / `mobileStyles` only when the entity defines them,
 * mirroring the FormGroup control base this path replaced (`initFormSubscription`
 * added each one conditionally).
 *
 * Widgets whose settings live in the envelope rather than in `data` — `line`
 * (thickness/style/colour/width) and `image` (border + radius group) — are
 * undoable only because of this; keying the entry on `data` alone silently
 * dropped every styles edit from the history stack.
 */
function widgetHistoryChanges(entity: WidgetSignalEntity | undefined): Partial<WebLegacyWidgetEntity> {
    const changes: Partial<WebLegacyWidgetEntity> = {
        data: entity?.data as any,
        displayId: (entity as { displayId?: WebLegacyWidgetEntity['displayId'] } | undefined)?.displayId,
    };

    if ((entity as { styles?: unknown } | undefined)?.styles !== undefined) {
        changes.styles = (entity as { styles?: unknown }).styles as WebLegacyWidgetEntity['styles'];
    }
    if ((entity as { mobileStyles?: unknown } | undefined)?.mobileStyles !== undefined) {
        changes.mobileStyles = (entity as { mobileStyles?: unknown })
            .mobileStyles as WebLegacyWidgetEntity['mobileStyles'];
    }

    return changes;
}

type WidgetFormEntry = {
    form: WidgetSignalForm;
    entitySignal: WritableSignal<WidgetSignalEntity>;
    storeSub: Subscription;
    refCount: number;
};

/**
 * Ref-counted registry of LEGACY widget signal forms (the pre-versioning
 * `{ name, columnId, data, styles }` envelope), keyed by widget id — the home
 * for every legacy signal-form widget (video, button, …). Versioned widgets
 * get their own service, `WebWidgetSignalFormService`. Replaces the per-widget `WebVideoFormService` /
 * `WebButtonFormService` twins.
 *
 * The control panel and the view component for the same widget share one
 * form instance; the form is destroyed once the last consumer releases.
 * Which factory builds the form is decided by the widget's `name` (read from
 * the store entity), so consumers only ever pass the widget id.
 *
 * The generic parameter is a typing convenience for call sites that know
 * their widget kind (`getOrCreateScoped<VideoForm>(…)`); the service itself
 * dispatches purely on the entity's `name`.
 */
@Injectable({ providedIn: 'root' })
export class WebLegacyWidgetSignalFormService {
    private readonly injector = inject(Injector);
    private readonly widgetsFacade = inject(WidgetsFacade);
    private readonly historyFacade = inject(HistoryFacade);
    private readonly widgetLoadFills = inject(WebWidgetLoadFillsService);

    private readonly registry = new Map<Uuid, WidgetFormEntry>();

    constructor() {
        (window as any).__webLegacyWidgetSignalFormService = this;
    }

    public getOrCreate<F extends WidgetSignalForm = WidgetSignalForm>(widgetId: Uuid): F | null {
        const existing = this.registry.get(widgetId);
        if (existing) {
            existing.refCount++;
            return existing.form as F;
        }

        const entitySignal = signal<WidgetSignalEntity>(undefined as unknown as WidgetSignalEntity);

        // NgRx selectors emit synchronously, so the signal is populated before form construction.
        // Every entity (initial and subsequent store emissions) is backfilled with
        // its variant defaults so older persisted widgets expose a complete shape.
        const storeSub = this.widgetsFacade
            .widgetById$(widgetId)
            .pipe(filter((widget): widget is WebLegacyWidgetEntity => Boolean(widget)))
            .subscribe((widget) => entitySignal.set(this.normalizeEntity(widget as unknown as WidgetSignalEntity)));

        // Read the entity synchronously: the widget's `name` picks the factory,
        // and `initialData.type` lets the factory's own dispatcher pick the
        // right variant (default vs zero) at construction time — the `entity`
        // signal alone fires only on the next effect tick.
        const initialEntity = entitySignal();

        const createForm = initialEntity ? FORM_FACTORIES[initialEntity.name as Widgets] : undefined;
        if (!createForm) {
            storeSub.unsubscribe();
            console.warn(
                `[WebLegacyWidgetSignalFormService] no signal-form factory for widget ${widgetId}` +
                    (initialEntity ? ` (name: ${initialEntity.name})` : ' (entity not in store yet)') +
                    `; register it in FORM_FACTORIES.`
            );
            return null;
        }

        const form = runInInjectionContext(this.injector, () =>
            createForm({
                entity: entitySignal,
                initialData: initialEntity,
                onUpdate: (value: WidgetSignalEntity, meta: { skipHistory: boolean }) => {
                    // Every `transactWithoutHistory` caller is a write the user did not make — a measurement
                    // on render, a sync with the CRM — so it must not save the page on its own either (#75695).
                    if (meta.skipHistory) {
                        this.widgetsFacade.syncLegacyWidgetProperties(value.id, value as any);
                        return;
                    }

                    const previous = untracked(() => entitySignal());
                    this.widgetLoadFills.markEdited(value.id, previous, value);
                    this.historyFacade.addWidgetUpdateChangeToHistory(
                        { id: value.id, changes: widgetHistoryChanges(previous) },
                        { id: value.id, changes: widgetHistoryChanges(value) }
                    );
                    this.widgetsFacade.updateLegacyWidgetProperties(value.id, value as any);
                },
                onCopy: (payload?: unknown) => {
                    const entity = untracked(() => entitySignal());
                    if (!entity) {
                        return;
                    }
                    const { index } = payload as WidgetCopyPayload;
                    const widget = entity as unknown as WebLegacyWidgetEntity;
                    if ((entity as { type?: unknown }).type === 'zero') {
                        this.widgetsFacade.copyZeroWidget(widget, index);
                    } else {
                        this.widgetsFacade.copyWidget(widget, index);
                    }
                },
                onRemove: (payload?: unknown) => {
                    const entity = untracked(() => entitySignal());
                    if (!entity) {
                        return;
                    }
                    const { index, skipHistory = false, skipUnselectEntity = false } = payload as WidgetRemovePayload;
                    this.widgetsFacade.removeWidget(
                        entity as unknown as WebLegacyWidgetEntity,
                        index,
                        skipHistory,
                        skipUnselectEntity
                    );
                },
            })
        );

        // Seed the form synchronously so consumers don't observe an empty form value.
        form.syncFromEntity(structuredClone(initialEntity) as any);

        this.registry.set(widgetId, { form, entitySignal, storeSub, refCount: 1 });
        return form as F;
    }

    public getOrCreateScoped<F extends WidgetSignalForm = WidgetSignalForm>(
        widgetId: Uuid,
        destroyRef: DestroyRef
    ): F | null {
        const form = this.getOrCreate<F>(widgetId);
        // Only register the release when a form was actually created/retained.
        // On failure `getOrCreate` touches no ref-count, so a destroy-time
        // release would decrement (and could destroy) a *different* consumer's
        // form that later registers under the same id.
        if (form) {
            destroyRef.onDestroy(() => this.release(widgetId));
        }
        return form;
    }

    public getForm<F extends WidgetSignalForm = WidgetSignalForm>(widgetId: Uuid): F | null {
        return (this.registry.get(widgetId)?.form as F) ?? null;
    }

    /**
     * Backfill a store entity with its variant defaults so the signal form sees
     * a complete shape. Widgets persisted before newer fields existed otherwise
     * have no FieldTree node for those keys, crashing reads/writes. Unknown
     * widget names (no default factory) pass through untouched.
     */
    private normalizeEntity(entity: WidgetSignalEntity): WidgetSignalEntity {
        return backfillWidgetEntity(entity);
    }

    public release(widgetId: Uuid): void {
        const entry = this.registry.get(widgetId);
        if (!entry) {
            return;
        }

        entry.refCount--;
        if (entry.refCount <= 0) {
            this.deleteForm(widgetId);
        }
    }

    public deleteForm(widgetId: Uuid): void {
        const entry = this.registry.get(widgetId);
        if (!entry) {
            return;
        }

        entry.storeSub.unsubscribe();
        entry.form.destroy();
        this.registry.delete(widgetId);
    }
}
